import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, BookOpen, Search, Trash2, Pencil, Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Default riding terminology with translations
const DEFAULT_WORDS = [
  { word_da: 'Schenkel', word_de: 'Schenkel', word_en: 'Leg aid', description: 'Benet/lår som hjælp' },
  { word_da: 'Schenkelvinding', word_de: 'Schenkelweichen', word_en: 'Leg yield', description: 'Sideværts bevægelse fra benet' },
  { word_da: 'Halvparade', word_de: 'Halbe Parade', word_en: 'Half halt', description: 'Kort, let hjælp' },
  { word_da: 'Kontergalop', word_de: 'Außengalopp', word_en: 'Counter canter', description: 'Galop på "forkert" ben' },
  { word_da: 'Traversale', word_de: 'Traversale', word_en: 'Half pass', description: 'Diagonal sideværts bevægelse' },
  { word_da: 'Renvers', word_de: 'Renvers', word_en: 'Renvers', description: 'Bagpartiet ind mod banen' },
  { word_da: 'Travers', word_de: 'Travers', word_en: 'Travers', description: 'Forpartiet ind mod banen' },
  { word_da: 'Volte', word_de: 'Volte', word_en: 'Volte', description: 'Lille cirkel' },
  { word_da: 'Zirkel', word_de: 'Zirkel', word_en: 'Circle', description: 'Stor cirkel' },
  { word_da: 'Skulder ind', word_de: 'Schulterherein', word_en: 'Shoulder in', description: 'Skulder ind' },
  { word_da: 'Piaffe', word_de: 'Piaffe', word_en: 'Piaffe', description: 'Trav på stedet' },
  { word_da: 'Passage', word_de: 'Passage', word_en: 'Passage', description: 'Ophøjet trav' },
  { word_da: 'Løshed', word_de: 'Losgelassenheit', word_en: 'Looseness', description: 'Afspænding/løshed' },
  { word_da: 'Anlæhning', word_de: 'Anlehnung', word_en: 'Contact', description: 'Kontakt til bidslet' },
  { word_da: 'Gennemridelighed', word_de: 'Durchlässigkeit', word_en: 'Throughness', description: 'Gennemridelighed' },
  { word_da: 'Samling', word_de: 'Versammlung', word_en: 'Collection', description: 'Samling' },
  { word_da: 'Svung', word_de: 'Schwung', word_en: 'Impulsion', description: 'Impulsion/svung' }
];

const VocabularyPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [words, setWords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  
  // New word form state
  const [newWordDa, setNewWordDa] = useState('');
  const [newWordDe, setNewWordDe] = useState('');
  const [newWordEn, setNewWordEn] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [activeLang, setActiveLang] = useState('da');
  
  // Edit state
  const [editingWord, setEditingWord] = useState(null);
  const [editWordDa, setEditWordDa] = useState('');
  const [editWordDe, setEditWordDe] = useState('');
  const [editWordEn, setEditWordEn] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editActiveLang, setEditActiveLang] = useState('da');

  useEffect(() => {
    fetchVocabulary();
  }, []);

  const fetchVocabulary = async () => {
    try {
      const response = await axios.get(`${API}/vocabulary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWords(response.data.words || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    } finally {
      setLoading(false);
    }
  };

  const addWord = async () => {
    if (!newWordDa.trim() && !newWordDe.trim() && !newWordEn.trim()) {
      toast.error('Indtast mindst ét ord');
      return;
    }

    try {
      const params = new URLSearchParams();
      if (newWordDa.trim()) params.append('word_da', newWordDa.trim());
      if (newWordDe.trim()) params.append('word_de', newWordDe.trim());
      if (newWordEn.trim()) params.append('word_en', newWordEn.trim());
      if (newDescription.trim()) params.append('description', newDescription.trim());
      
      await axios.post(`${API}/vocabulary/add?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const displayWord = newWordDa || newWordDe || newWordEn;
      toast.success(`"${displayWord}" tilføjet`);
      resetAddForm();
      fetchVocabulary();
    } catch (error) {
      console.error('Error adding word:', error);
      toast.error('Kunne ikke tilføje ord');
    }
  };

  const resetAddForm = () => {
    setNewWordDa('');
    setNewWordDe('');
    setNewWordEn('');
    setNewDescription('');
    setActiveLang('da');
    setShowAddForm(false);
  };

  const deleteWord = async (word) => {
    const displayName = word.word_da || word.word_de || word.word_en || word.word;
    if (!window.confirm(`Slet "${displayName}"?`)) return;

    try {
      const wordToDelete = word.word || word.word_da || word.word_de || word.word_en;
      await axios.delete(`${API}/vocabulary/${encodeURIComponent(wordToDelete)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`"${displayName}" slettet`);
      fetchVocabulary();
    } catch (error) {
      console.error('Error deleting word:', error);
      toast.error('Kunne ikke slette ord');
    }
  };

  const startEditing = (item) => {
    const wordKey = item.word || item.word_da || item.word_de || item.word_en;
    setEditingWord(wordKey);
    setEditWordDa(item.word_da || item.word || '');
    setEditWordDe(item.word_de || '');
    setEditWordEn(item.word_en || '');
    setEditDescription(item.description || '');
    setEditActiveLang('da');
  };

  const cancelEditing = () => {
    setEditingWord(null);
    setEditWordDa('');
    setEditWordDe('');
    setEditWordEn('');
    setEditDescription('');
    setEditActiveLang('da');
  };

  const saveEdit = async () => {
    if (!editWordDa.trim() && !editWordDe.trim() && !editWordEn.trim()) {
      toast.error('Mindst ét ord skal udfyldes');
      return;
    }

    try {
      const params = new URLSearchParams();
      params.append('word_da', editWordDa.trim());
      params.append('word_de', editWordDe.trim());
      params.append('word_en', editWordEn.trim());
      params.append('description', editDescription.trim());
      
      await axios.put(`${API}/vocabulary/${encodeURIComponent(editingWord)}?${params.toString()}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Ord opdateret');
      cancelEditing();
      fetchVocabulary();
    } catch (error) {
      console.error('Error updating word:', error);
      toast.error('Kunne ikke opdatere ord');
    }
  };

  const addDefaultWords = async () => {
    if (!window.confirm(`Tilføj ${DEFAULT_WORDS.length} standard ridetermer med oversættelser?`)) return;

    try {
      for (const item of DEFAULT_WORDS) {
        const params = new URLSearchParams();
        if (item.word_da) params.append('word_da', item.word_da);
        if (item.word_de) params.append('word_de', item.word_de);
        if (item.word_en) params.append('word_en', item.word_en);
        if (item.description) params.append('description', item.description);
        
        await axios.post(`${API}/vocabulary/add?${params.toString()}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      toast.success(`${DEFAULT_WORDS.length} ord tilføjet`);
      fetchVocabulary();
    } catch (error) {
      console.error('Error adding default words:', error);
      toast.error('Kunne ikke tilføje alle ord');
      fetchVocabulary();
    }
  };

  const filteredWords = words.filter(w => {
    const query = searchQuery.toLowerCase();
    return (
      (w.word && w.word.toLowerCase().includes(query)) ||
      (w.word_da && w.word_da.toLowerCase().includes(query)) ||
      (w.word_de && w.word_de.toLowerCase().includes(query)) ||
      (w.word_en && w.word_en.toLowerCase().includes(query)) ||
      (w.description && w.description.toLowerCase().includes(query))
    );
  });

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Indlæser...</div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f3f0', padding: '1rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
          <button
            onClick={() => navigate('/admin')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', display: 'flex', alignItems: 'center' }}
          >
            <ArrowLeft size={24} />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: '1.5rem', color: '#333' }}>
              📚 Ordliste
            </h1>
            <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
              {words.length} ord til bedre transskribering
            </p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1rem',
              background: '#6b5e8e',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 500
            }}
          >
            <Plus size={18} />
            Tilføj ord
          </button>
        </div>

        {/* Info box */}
        <div style={{ 
          background: '#e8f5e9', 
          borderRadius: '12px', 
          padding: '1rem', 
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '0.75rem'
        }}>
          <BookOpen size={20} style={{ color: '#4caf50', marginTop: '0.15rem' }} />
          <div style={{ fontSize: '0.9rem', color: '#2e7d32' }}>
            <strong>Hvordan virker det?</strong><br />
            Disse ord hjælper AI'en med at genkende specialiserede ridetermer under transskribering. 
            Tilføj ord som du ofte bruger i undervisningen - f.eks. "Schenkel", "Schenkelvinding", "Traversale".
          </div>
        </div>

        {/* Search */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '1rem', 
          marginBottom: '1rem',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
        }}>
          <div style={{ position: 'relative' }}>
            <Search size={20} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              placeholder="Søg i ordliste..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.75rem',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem'
              }}
            />
          </div>
        </div>

        {/* Word list */}
        {filteredWords.length === 0 ? (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            padding: '3rem', 
            textAlign: 'center', 
            color: '#666',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            {words.length === 0 ? (
              <>
                <BookOpen size={48} style={{ marginBottom: '1rem', color: '#ccc' }} />
                <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>Ingen ord endnu</p>
                <p style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>
                  Tilføj dine egne ord eller start med standard ridetermer
                </p>
                <button
                  onClick={addDefaultWords}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#6b5e8e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}
                >
                  Tilføj standard ridetermer
                </button>
              </>
            ) : (
              <p>Ingen ord matcher søgningen</p>
            )}
          </div>
        ) : (
          <div style={{ 
            background: 'white', 
            borderRadius: '12px', 
            overflow: 'hidden',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            {filteredWords.map((item, index) => {
              const wordKey = item.word || item.word_da || item.word_de || item.word_en;
              const isEditing = editingWord === wordKey;
              
              return (
              <div
                key={wordKey}
                style={{
                  padding: '1rem',
                  borderBottom: index < filteredWords.length - 1 ? '1px solid #f0f0f0' : 'none'
                }}
              >
                {isEditing ? (
                  // Edit mode with language tabs
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {/* Language tabs */}
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      {[
                        { code: 'da', flag: '🇩🇰' },
                        { code: 'de', flag: '🇩🇪' },
                        { code: 'en', flag: '🇬🇧' }
                      ].map(lang => (
                        <button
                          key={lang.code}
                          onClick={() => setEditActiveLang(lang.code)}
                          style={{
                            flex: 1,
                            padding: '0.4rem',
                            border: editActiveLang === lang.code ? '2px solid #6b5e8e' : '1px solid #e0e0e0',
                            borderRadius: '6px',
                            background: editActiveLang === lang.code ? '#f5f0fa' : 'white',
                            cursor: 'pointer',
                            fontSize: '1.1rem'
                          }}
                        >
                          {lang.flag}
                        </button>
                      ))}
                    </div>
                    
                    <input
                      type="text"
                      value={editActiveLang === 'da' ? editWordDa : editActiveLang === 'de' ? editWordDe : editWordEn}
                      onChange={(e) => {
                        if (editActiveLang === 'da') setEditWordDa(e.target.value);
                        else if (editActiveLang === 'de') setEditWordDe(e.target.value);
                        else setEditWordEn(e.target.value);
                      }}
                      placeholder={editActiveLang === 'da' ? 'Dansk ord' : editActiveLang === 'de' ? 'Deutsches Wort' : 'English word'}
                      style={{
                        padding: '0.6rem 0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}
                      autoFocus
                    />
                    
                    {/* Show all filled languages */}
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      {editWordDa && <span style={{ background: '#e8f5e9', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.8rem' }}>🇩🇰 {editWordDa}</span>}
                      {editWordDe && <span style={{ background: '#e3f2fd', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.8rem' }}>🇩🇪 {editWordDe}</span>}
                      {editWordEn && <span style={{ background: '#fff3e0', padding: '0.2rem 0.4rem', borderRadius: '4px', fontSize: '0.8rem' }}>🇬🇧 {editWordEn}</span>}
                    </div>
                    
                    <input
                      type="text"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Beskrivelse (valgfri)"
                      style={{
                        padding: '0.6rem 0.75rem',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        fontSize: '0.85rem'
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={cancelEditing}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: '#f5f5f5',
                          color: '#666',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 500
                        }}
                      >
                        Annuller
                      </button>
                      <button
                        onClick={saveEdit}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          background: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.25rem'
                        }}
                      >
                        <Check size={16} />
                        Gem
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode - show all languages
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{ flex: 1 }}>
                      {/* Show languages with flags */}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: item.description ? '0.35rem' : 0 }}>
                        {(item.word_da || item.word) && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: 600, color: '#333' }}>
                            🇩🇰 {item.word_da || item.word}
                          </span>
                        )}
                        {item.word_de && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#555' }}>
                            🇩🇪 {item.word_de}
                          </span>
                        )}
                        {item.word_en && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', color: '#555' }}>
                            🇬🇧 {item.word_en}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <div style={{ fontSize: '0.8rem', color: '#999' }}>
                          {item.description}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.35rem' }}>
                      <button
                        onClick={() => startEditing(item)}
                        style={{
                          background: '#fff3e0',
                          color: '#f57c00',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.4rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Ret ord"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        onClick={() => deleteWord(item)}
                        style={{
                          background: '#ffebee',
                          color: '#c62828',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '0.4rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                        title="Slet ord"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )})}
          </div>
        )}

        {/* Add default words button (if some words exist but not all defaults) */}
        {words.length > 0 && words.length < 10 && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              onClick={addDefaultWords}
              style={{
                padding: '0.5rem 1rem',
                background: 'none',
                color: '#6b5e8e',
                border: '1px solid #6b5e8e',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              + Tilføj standard ridetermer
            </button>
          </div>
        )}

        {/* Add word form modal */}
        {showAddForm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '400px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Tilføj nyt ord</h2>
                <button
                  onClick={resetAddForm}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666' }}
                >
                  <X size={24} />
                </button>
              </div>

              {/* Language tabs */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                {[
                  { code: 'da', flag: '🇩🇰', label: 'Dansk' },
                  { code: 'de', flag: '🇩🇪', label: 'Deutsch' },
                  { code: 'en', flag: '🇬🇧', label: 'English' }
                ].map(lang => (
                  <button
                    key={lang.code}
                    onClick={() => setActiveLang(lang.code)}
                    style={{
                      flex: 1,
                      padding: '0.5rem',
                      border: activeLang === lang.code ? '2px solid #6b5e8e' : '1px solid #e0e0e0',
                      borderRadius: '8px',
                      background: activeLang === lang.code ? '#f5f0fa' : 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <span style={{ fontSize: '1.25rem' }}>{lang.flag}</span>
                    <span style={{ fontSize: '0.7rem', color: '#666' }}>
                      {lang.code === 'da' ? (newWordDa || '-') : lang.code === 'de' ? (newWordDe || '-') : (newWordEn || '-')}
                    </span>
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    {activeLang === 'da' ? '🇩🇰 Dansk ord' : activeLang === 'de' ? '🇩🇪 Tysk ord' : '🇬🇧 Engelsk ord'}
                  </label>
                  <input
                    type="text"
                    value={activeLang === 'da' ? newWordDa : activeLang === 'de' ? newWordDe : newWordEn}
                    onChange={(e) => {
                      if (activeLang === 'da') setNewWordDa(e.target.value);
                      else if (activeLang === 'de') setNewWordDe(e.target.value);
                      else setNewWordEn(e.target.value);
                    }}
                    placeholder={activeLang === 'da' ? 'F.eks. Schenkelvinding' : activeLang === 'de' ? 'Z.B. Schenkelweichen' : 'E.g. Leg yield'}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '1rem'
                    }}
                    autoFocus
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500, color: '#666' }}>
                    Beskrivelse (valgfri)
                  </label>
                  <input
                    type="text"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder="Kort forklaring..."
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      border: '1px solid #e0e0e0',
                      borderRadius: '8px',
                      fontSize: '0.9rem'
                    }}
                  />
                </div>

                {/* Show filled languages */}
                <div style={{ 
                  padding: '0.75rem', 
                  background: '#f9f9f9', 
                  borderRadius: '8px',
                  fontSize: '0.85rem'
                }}>
                  <div style={{ fontWeight: 500, marginBottom: '0.5rem', color: '#666' }}>Udfyldte sprog:</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {newWordDa && <span style={{ background: '#e8f5e9', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>🇩🇰 {newWordDa}</span>}
                    {newWordDe && <span style={{ background: '#e3f2fd', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>🇩🇪 {newWordDe}</span>}
                    {newWordEn && <span style={{ background: '#fff3e0', padding: '0.25rem 0.5rem', borderRadius: '4px' }}>🇬🇧 {newWordEn}</span>}
                    {!newWordDa && !newWordDe && !newWordEn && <span style={{ color: '#999' }}>Ingen</span>}
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={resetAddForm}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: '#f5f5f5',
                      color: '#666',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    Annuller
                  </button>
                  <button
                    onClick={addWord}
                    disabled={!newWordDa && !newWordDe && !newWordEn}
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      background: (newWordDa || newWordDe || newWordEn) ? '#4caf50' : '#e0e0e0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: (newWordDa || newWordDe || newWordEn) ? 'pointer' : 'not-allowed',
                      fontWeight: 500
                    }}
                  >
                    Tilføj ord
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VocabularyPage;
